import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import {
  ArrowRight,
  Mail,
  Sparkles,
  LifeBuoy,
  Briefcase,
  HelpCircle,
  Handshake,
  ShieldCheck,
  Inbox,
} from "lucide-react";

// Public /contact page. Postmoro's public contact channel is
// support@postmoro.com — the same address exposed in every legal /
// footer surface. Per the original P1 spec ("If there is NOT a real
// contact-form backend, do NOT invent one"), we intentionally keep
// this page email-first with no fake form. Data-deletion has its own
// dedicated page and is linked below.

const SECTIONS = [
  {
    icon: LifeBuoy,
    tone: "primary",
    title: "Support",
    body: "Trouble with connections, publishing, billing, or your account? Email us and we'll get back to you as quickly as we can.",
    testid: "contact-section-support",
  },
  {
    icon: Briefcase,
    tone: "secondary",
    title: "Business inquiries",
    body: "Partnerships, agency plans, custom integrations, or reseller questions — reach out and let us know what you're building.",
    testid: "contact-section-business",
  },
  {
    icon: Handshake,
    tone: "primary",
    title: "Affiliates",
    body: "Interested in referring customers to Postmoro? Read the Affiliate Program page for the current terms, or email us with any questions.",
    testid: "contact-section-affiliates",
    linkTo: "/affiliates",
    linkLabel: "See affiliate terms",
  },
  {
    icon: HelpCircle,
    tone: "cool",
    title: "General questions",
    body: "Not sure which plan fits, or wondering whether Postmoro supports a specific platform or workflow? We're happy to help before you sign up.",
    testid: "contact-section-general",
  },
];

function toneClasses(tone) {
  if (tone === "secondary") {
    return {
      chip:
        "bg-[color:rgba(34,211,238,0.10)] border-[color:rgba(34,211,238,0.45)] text-[color:var(--brand-secondary)]",
      link: "text-[color:var(--brand-secondary)]",
      glow: "rgba(34,211,238,0.14)",
    };
  }
  if (tone === "cool") {
    return {
      chip:
        "bg-[color:rgba(47,143,229,0.10)] border-[color:rgba(47,143,229,0.45)] text-[color:#8fb4f5]",
      link: "text-[color:var(--brand-primary)]",
      glow: "rgba(47,143,229,0.14)",
    };
  }
  return {
    chip:
      "bg-[color:rgba(27,168,159,0.10)] border-[color:rgba(27,168,159,0.45)] text-[color:var(--brand-primary)]",
    link: "text-[color:var(--brand-primary)]",
    glow: "rgba(27,168,159,0.18)",
  };
}

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact Postmoro | Support & Business Inquiries"
        description="Contact Postmoro for support, product questions, or business inquiries. Reach the Postmoro team by email at support@postmoro.com."
        path="/contact"
      />
      <div
        className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] relative overflow-hidden"
        data-testid="contact-page"
      >
        {/* Ambient background layers */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-0">
          <div
            className="absolute -top-40 right-1/3 h-[520px] w-[720px] rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.20), transparent 70%)" }}
          />
          <div
            className="absolute top-[320px] -left-40 h-[420px] w-[520px] rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(47,143,229,0.14), transparent 70%)" }}
          />
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.10) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage: "radial-gradient(ellipse at 70% 10%, black 30%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse at 70% 10%, black 30%, transparent 70%)",
            }}
          />
        </div>

        <MarketingHeader activePath="/contact" />

        {/* ============ HERO ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-8 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              Contact
            </div>
            <h1
              className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]"
              data-testid="contact-hero-title"
            >
              How can we help?
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              We're a small team. Email is the fastest way to reach us — one
              inbox handles support, product questions, and business inquiries.
            </p>
            <a
              href="mailto:support@postmoro.com"
              className="mt-7 inline-flex"
              data-testid="contact-email-cta"
            >
              <Button
                size="lg"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              >
                <Mail className="mr-2 h-4 w-4" /> support@postmoro.com
              </Button>
            </a>

            {/* Trust row — all claims are TRUE for Postmoro today. */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {[
                { icon: Inbox, label: "One inbox for everything" },
                { icon: ShieldCheck, label: "Real humans read every email" },
                { icon: Mail, label: "support@postmoro.com" },
              ].map((t) => (
                <span
                  key={t.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-3 py-1 text-[11px] text-[color:var(--text-2)]"
                >
                  <t.icon className="h-3 w-3 text-[color:var(--brand-primary)]" />
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 4-UP OPTIONS ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECTIONS.map((s) => {
              const t = toneClasses(s.tone);
              return (
                <div
                  key={s.title}
                  className="relative rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 overflow-hidden flex flex-col"
                  data-testid={s.testid}
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-14 -right-14 h-32 w-32 rounded-full"
                    style={{ background: `radial-gradient(closest-side, ${t.glow}, transparent 70%)` }}
                  />
                  <div className={"relative h-10 w-10 rounded-[10px] border flex items-center justify-center " + t.chip}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="relative mt-4 font-display text-lg font-semibold">{s.title}</h3>
                  <p className="relative mt-2 text-sm text-[color:var(--text-2)] leading-relaxed flex-1">
                    {s.body}
                  </p>
                  {s.linkTo ? (
                    <Link
                      to={s.linkTo}
                      className={"relative mt-4 inline-flex text-sm hover:underline " + t.link}
                    >
                      {s.linkLabel || "Learn more"} <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  ) : (
                    <a
                      href="mailto:support@postmoro.com"
                      className={"relative mt-4 inline-flex text-sm hover:underline " + t.link}
                    >
                      Email us <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ============ WHAT TO EXPECT — truthful only ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 sm:p-8">
            <p className="eyebrow text-[color:var(--brand-primary)]">What happens after you email us</p>
            <h2 className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight">
              A real person reads it, and replies.
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-[color:var(--text-2)]">
              <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Step 1</div>
                <div className="font-display text-base font-semibold text-[color:var(--text-1)] mt-1">You email us</div>
                <p className="mt-2 leading-relaxed">Send anything to <a href="mailto:support@postmoro.com" className="text-[color:var(--brand-primary)] hover:underline">support@postmoro.com</a> — support, sales, feedback, or bugs.</p>
              </div>
              <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-secondary)]">Step 2</div>
                <div className="font-display text-base font-semibold text-[color:var(--text-1)] mt-1">We read it</div>
                <p className="mt-2 leading-relaxed">One shared inbox. No bots, no chat maze. A real Postmoro team member picks it up.</p>
              </div>
              <div className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Step 3</div>
                <div className="font-display text-base font-semibold text-[color:var(--text-1)] mt-1">We reply</div>
                <p className="mt-2 leading-relaxed">Usually within a working day. If your question is billing or account related, please include the email address on your Postmoro account.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ COMPANY INFO (unchanged data) ============ */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <div
            className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6"
            data-testid="contact-company-info"
          >
            <h2 className="font-display text-xl font-semibold">Postmoro</h2>
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
              <div>
                <dt className="text-[color:var(--text-3)]">Legal entity</dt>
                <dd className="text-[color:var(--text-1)]">Postmoro LLC</dd>
              </div>
              <div>
                <dt className="text-[color:var(--text-3)]">Website</dt>
                <dd>
                  <a
                    className="text-[color:var(--brand-primary)] hover:underline"
                    href="https://postmoro.com/"
                  >
                    postmoro.com
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-[color:var(--text-3)]">Support email</dt>
                <dd>
                  <a
                    className="text-[color:var(--brand-primary)] hover:underline"
                    href="mailto:support@postmoro.com"
                  >
                    support@postmoro.com
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-[color:var(--text-3)]">Data deletion</dt>
                <dd>
                  <Link
                    className="text-[color:var(--brand-primary)] hover:underline"
                    to="/data-deletion"
                  >
                    postmoro.com/data-deletion
                  </Link>
                </dd>
              </div>
            </dl>
            <div className="mt-6 flex flex-wrap gap-3 text-xs text-[color:var(--text-3)]">
              <a
                href="https://www.linkedin.com/company/postmoro"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
              >
                LinkedIn
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://x.com/Postmoro"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
              >
                X
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://www.facebook.com/profile.php?id=61590293413413"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
              >
                Facebook
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://www.instagram.com/postmoroofficial"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[color:var(--text-1)]"
              >
                Instagram
              </a>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
